import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

export interface FilterConfig {
  name: string;
  description?: string;
  defaultValue?: any;
  updateType?: 'debouncing' | 'submit';
  debounceTime?: number;
}

export interface FilterFunction {
  name: string;
  description?: string;
  getFilter: () => Observable<any>;
  setFilter: (value: any) => void;
  config: FilterConfig;
  ready$?: Observable<boolean>; // Observable that emits when the filter is ready
}

@Injectable({
  providedIn: 'root'
})
export class ComponentDataFilterService {
  private filterRegistry = new Map<string, FilterFunction>();
  private registeredFilters: string[] = [];

  constructor(
    private http: HttpClient
  ) {
    // Initialize the service
  }

  /**
   * Register a filter with the service
   * @param key The unique identifier for the filter
   * @param filter The filter function
   */
  registerFilter(key: string, filter: FilterFunction): void {
    if (this.filterRegistry.has(key)) {
      console.warn(`[ComponentDataFilterService] Filter with key ${key} already exists. Overwriting.`);
    }
    
    this.filterRegistry.set(key, filter);
    
    if (!this.registeredFilters.includes(key)) {
      this.registeredFilters.push(key);
    }
    
  }

  /**
   * Get a filter by key
   * @param key The unique identifier for the filter
   * @returns The filter function or undefined if not found
   */
  getFilter(key: string): FilterFunction | undefined {
    const filter = this.filterRegistry.get(key);
    
    if (!filter) {
      console.warn(`[ComponentDataFilterService] Filter with key ${key} not found`);
    }
    
    return filter;
  }

  /**
   * Get all registered filter keys
   * @returns Array of filter keys
   */
  getRegisteredFilters(): string[] {
    return [...this.registeredFilters];
  }

  /**
   * Create a new filter function
   * @param name The name of the filter
   * @param description Optional description
   * @param defaultValue Optional default value
   * @param updateType How the filter should be updated ('debouncing' or 'submit')
   * @param debounceTime Optional debounce time in milliseconds (default: 1000)
   * @returns A filter function
   */
  createFilter(
    name: string, 
    description?: string, 
    defaultValue: any = {}, 
    updateType: 'debouncing' | 'submit' = 'debouncing',
    debounceTime: number = 1000
  ): FilterFunction {
    const filterSubject = new BehaviorSubject<any>(defaultValue);
    
    return {
      name,
      description,
      getFilter: () => filterSubject.asObservable(),
      setFilter: (value: any) => filterSubject.next(value),
      config: {
        name,
        description,
        defaultValue,
        updateType,
        debounceTime
      }
    };
  }
}
